import React, { useState } from "react";
import {
  Box,
  FormControl,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import type { SelectChangeEvent } from "@mui/material/Select";
import { ExpandMoreOutlined } from "@mui/icons-material";
import type { WorkspaceDefinition, WorkspaceId } from "../../utils/workspaces";

const NAV_ICON_SX = { fontSize: 22 } as const;

interface WorkspaceSwitcherProps {
  available: WorkspaceDefinition[];
  active: WorkspaceDefinition | null;
  onSelect: (id: WorkspaceId) => void;
  collapsed?: boolean;
  variant?: "sidebar" | "compact";
}

const WorkspaceSwitcher: React.FC<WorkspaceSwitcherProps> = ({
  available,
  active,
  onSelect,
  collapsed = false,
  variant = "sidebar",
}) => {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  if (available.length === 0 || !active) {
    return null;
  }

  const currentLabel = active.label;
  const CurrentIcon = active.icon;

  const handleSelect = (id: WorkspaceId) => {
    setMenuAnchor(null);
    if (id === active.id) return;
    onSelect(id);
  };

  const handleChange = (event: SelectChangeEvent<string>) => {
    handleSelect(event.target.value as WorkspaceId);
  };

  const openMenu = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(event.currentTarget);
  };

  const closeMenu = () => setMenuAnchor(null);

  const menuItems = available.map((ws) => {
    const Icon = ws.icon;
    return (
      <MenuItem
        key={ws.id}
        selected={ws.id === active.id}
        onClick={() => handleSelect(ws.id)}
      >
        <ListItemIcon sx={{ minWidth: 36 }}>
          <Icon sx={NAV_ICON_SX} />
        </ListItemIcon>
        <Box>
          <Typography variant="body2" fontWeight={600}>
            {ws.label}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {ws.description}
          </Typography>
        </Box>
      </MenuItem>
    );
  });

  if (variant === "compact") {
    return (
      <>
        <Tooltip title={`Workspace: ${currentLabel}`}>
          <IconButton
            size="small"
            onClick={openMenu}
            aria-label={`Switch workspace (${currentLabel})`}
          >
            <CurrentIcon sx={NAV_ICON_SX} />
          </IconButton>
        </Tooltip>
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={closeMenu}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          {menuItems}
        </Menu>
      </>
    );
  }

  if (collapsed) {
    return (
      <>
        <Tooltip title={`Workspace: ${currentLabel}`} placement="right">
          <IconButton
            size="small"
            onClick={openMenu}
            aria-label={`Switch workspace (${currentLabel})`}
            sx={{ display: "flex", mx: "auto", my: 0.5 }}
          >
            <CurrentIcon sx={NAV_ICON_SX} />
          </IconButton>
        </Tooltip>
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={closeMenu}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
        >
          {menuItems}
        </Menu>
      </>
    );
  }

  return (
    <Box sx={{ px: 2, pb: 1.5 }}>
      <Typography
        variant="caption"
        sx={{ display: "block", color: "text.secondary", mb: 0.5 }}
      >
        Workspace
      </Typography>
      <FormControl fullWidth size="small">
        <Select
          value={active.id}
          onChange={handleChange}
          renderValue={() => (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CurrentIcon sx={{ fontSize: 20 }} />
              <Typography noWrap variant="body2" fontWeight={500}>
                {currentLabel}
              </Typography>
            </Box>
          )}
          IconComponent={ExpandMoreOutlined}
          sx={{
            fontSize: "0.875rem",
            "& .MuiSelect-select": {
              py: 1,
              display: "flex",
              alignItems: "center",
            },
          }}
        >
          {available.map((ws) => {
            const Icon = ws.icon;
            return (
              <MenuItem key={ws.id} value={ws.id}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Icon sx={NAV_ICON_SX} />
                </ListItemIcon>
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {ws.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {ws.description}
                  </Typography>
                </Box>
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
    </Box>
  );
};

export default WorkspaceSwitcher;
